'use client';

import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from '@packages/modules/src/Link/Link';
import {
  EnergyEfficiencyFlag,
  type EnergyEfficiencyFlagProps,
} from '@packages/shared/src/ui/EnergyEfficiencyFlag/EnergyEfficiencyFlag';
import { colorsForEnergyEfficiency } from '@packages/shared/src/ui/EnergyEfficiencyFlag/colorsForEnergyEfficiency';
import { TireEfficiencyFlag as TireEfficiencyFlagUi } from '@packages/shared/src/ui/TireEfficiencyFlag/TireEfficiencyFlag';
import { EfficiencyFlagOverlayDialog } from '@packages/modules/src/EfficiencyFlagOverlayDialog/EfficiencyFlagOverlayDialog';
import { powerEfficiencyFlagMessages } from '@packages/modules/src/EfficiencyFlagList/PowerEfficiencyFlag/messages';
import { tireEfficiencyFlagMessages } from '@packages/modules/src/EfficiencyFlagList/TireEfficiencyFlag/messages';

type EfficiencyClass = keyof typeof colorsForEnergyEfficiency;

/**
 * Single power efficiency flag entry — typed as plain props so basket, wishlist
 * and AddToBasketLayer can map their slightly different GraphQL shapes
 * (`BasketPowerEfficiencyFlag`, `WishlistProductEnergyEfficiency`) into the same component.
 */
export type EfficiencyFlagsPowerFlag = {
  /**
   * EU energy class letter A-G. Items whose level is not a valid class
   * (e.g. legacy backend behaviour where tire data was smuggled into power flags
   * with composite levels like "B | 71 dB", or wishlist nullable shape) are silently skipped.
   */
  level: string | null;
  /** Localized label, used as overlay dialog title (e.g. "Energieeffizienzklasse"). */
  text: string | null;
  /** Image URL shown in the overlay dialog. */
  link: string;
  /** Optional product data sheet URL, rendered as an inline download link. */
  dataSheet?: string | null;
};

/**
 * Single tire efficiency flag entry — plain props matching the PascalCase enum
 * used by both `BasketProduct.tireEfficiencyFlags` (`CheckoutTireEfficiencyFlag`)
 * and `WishlistLineItem.tireEfficiency` (`WishlistTireEfficiencyLabel`).
 */
export type EfficiencyFlagsTireFlag = {
  type: 'FuelEfficiency' | 'WetGrip' | 'ExternalRollingNoise';
  /** Displayed text (e.g. "B" or "B | 71 dB" for rolling noise). */
  level: string;
  /** Image URL shown in the overlay dialog. */
  link: string;
};

export type EfficiencyFlagsProps = {
  powerFlags?: readonly EfficiencyFlagsPowerFlag[] | null;
  tireFlags?: readonly EfficiencyFlagsTireFlag[] | null;
  /**
   * Optional product data sheet URL rendered once below the tire flag row
   * (consistent with how the previous `<PowerEfficiency>` component rendered it for tires).
   */
  tireDataSheet?: string | null;
};

const isEfficiencyClass = (value: string | null | undefined): value is EfficiencyClass =>
  !!value && value in colorsForEnergyEfficiency;

const tireUiTypeMap = {
  FuelEfficiency: 'fuelEfficiency',
  WetGrip: 'wetGrip',
  ExternalRollingNoise: 'externalRollingNoise',
} as const;

const tireDialogTitleKeyMap = {
  FuelEfficiency: 'FUELEFFICIENCY',
  WetGrip: 'WETGRIP',
  ExternalRollingNoise: 'EXTERNALROLLINGNOISE',
} as const;

const DataSheetLink = ({ href }: { href: string }): ReactNode => (
  <Link className="text-size-body-xs underline" href={href} target="_blank" rel="noreferrer">
    <FormattedMessage id="LineItem.PowerEfficiency.DataSheet" defaultMessage="Produktdatenblatt" />
  </Link>
);

const PowerFlagItem = ({
  flag,
  size,
}: {
  flag: EfficiencyFlagsPowerFlag;
  size: NonNullable<EnergyEfficiencyFlagProps['size']>;
}) => {
  const { formatMessage } = useIntl();

  if (!isEfficiencyClass(flag.level)) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      <EfficiencyFlagOverlayDialog overlay={{ title: flag.text ?? '', imgUrl: flag.link }}>
        <EnergyEfficiencyFlag
          variant="new"
          efficiencyClass={flag.level}
          size={size}
          aria-label={formatMessage(powerEfficiencyFlagMessages.ariaLabel, {
            energyClass: flag.level,
          })}
        />
      </EfficiencyFlagOverlayDialog>
      {flag.dataSheet ? <DataSheetLink href={flag.dataSheet} /> : null}
    </div>
  );
};

const TireFlagItem = ({ flag }: { flag: EfficiencyFlagsTireFlag }) => {
  const { formatMessage } = useIntl();
  const titleKey = tireDialogTitleKeyMap[flag.type];

  return (
    <EfficiencyFlagOverlayDialog
      overlay={{
        title:
          titleKey in tireEfficiencyFlagMessages
            ? formatMessage(tireEfficiencyFlagMessages[titleKey])
            : '',
        imgUrl: flag.link,
      }}
    >
      <TireEfficiencyFlagUi type={tireUiTypeMap[flag.type]} text={flag.level} size="normal" />
    </EfficiencyFlagOverlayDialog>
  );
};

/**
 * Composite renderer for power and tire efficiency flags used across basket, wishlist
 * and AddToBasketLayer line items. Built on the shared UI primitives
 * `<EnergyEfficiencyFlag>` and `<TireEfficiencyFlag>` so all three line-item surfaces
 * use the same visuals as the rest of the shop (PDP, ProductCardV2).
 */
export const EfficiencyFlags = ({ powerFlags, tireFlags, tireDataSheet }: EfficiencyFlagsProps) => {
  const renderablePowerFlags = powerFlags?.filter((f) => isEfficiencyClass(f.level)) ?? [];
  const hasPower = renderablePowerFlags.length > 0;
  const hasTire = !!tireFlags && tireFlags.length > 0;

  if (!hasPower && !hasTire) return null;

  return (
    <div className="flex flex-col items-start gap-1">
      {hasPower && !hasTire && (
        <div className="flex flex-col items-start gap-1">
          {renderablePowerFlags.map((flag, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <PowerFlagItem key={`p-${idx}-${flag.text}-${flag.level}`} flag={flag} size="large" />
          ))}
        </div>
      )}
      {hasTire && (
        <div className="flex flex-row flex-wrap items-center gap-1">
          {tireFlags.map((flag, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <TireFlagItem key={`t-${idx}-${flag.type}-${flag.level}`} flag={flag} />
          ))}
        </div>
      )}
      {hasTire && tireDataSheet ? <DataSheetLink href={tireDataSheet} /> : null}
    </div>
  );
};
